// Two tabs of the candidate profile that write (lane B3 S12): Talent community — the membership's segment, tags,
// reason/date added, last contact, notes and prior requisition (the Recruiter records the last contact; no campaign
// control anywhere; the capture rule stays in the documents, UAT F-32) — and Internal mobility — "Capture current department/title/leader/location/
// grade and mobility type." with the self-attestation "that current manager was informed; do not auto-notify manager."
// (there is no notify control). Grade and leader show only where the reader's field class reaches them. Mobility type
// is a taxonomy the Admin publishes (CR-066): with none published the field stays, dashed and described by his sentence.
// Every day reads in the app's date format in the reader's time zone, never ISO (UAT F-73).

import { useState } from "react";
import { AppError } from "@/api/runtime";
import type { CandidateProfileOutput, InternalOut } from "@/api/tools";
import { candidateCommunityTouch, candidateRecordAttestation, candidateSetInternalProfile } from "@/api/tools";
import { useWrite } from "@/api/useTool";
import { go, useRoute } from "@/router";
import { useSession } from "@/session/useSession";
import {
  ACTION_LABELS, Button, Card, CheckTile, COMMON_LABELS, DEFAULT_TIME_ZONE, EmptyState, ErrorStrip, Field, Flex, formatDate, Grid, Input, KeyValue,
  NAV_ITEM_LABELS, Recorded, RULE_LABELS, Stack,
  type KeyValueItem,
} from "@/mc";
import { CANDIDATE_LABELS as L, PROFILE_LABELS as P, PROFILE_RULES } from "./labels";
import { queryOf, withQuery } from "@/views/shared/settledRead";
import { profileHref, reasonWord, requisitionWord } from "./profile";
import { TaxonomySelect, useTaxonomy } from "./ProfileDialogs";
import type { ProfileSubject } from "./ProfilePage";
import { refusalSentence } from "./refusals";

const dash = (value: string | null | undefined): string => (value ? value : "—");
/** A day in the app's format, in the reader's time zone. */
function useDay(): (iso: string | null | undefined) => string {
  const timeZone = useSession().me?.timezone || DEFAULT_TIME_ZONE;
  return (iso) => (iso ? formatDate(iso, timeZone) : "—");
}

/** A write's outcome on a tab: his sentence for a refusal, the error strip for a failure. */
function useOutcome() {
  const [refused, setRefused] = useState<string | null>(null);
  const [failure, setFailure] = useState<AppError | null>(null);
  const [done, setDone] = useState(false);
  const run = async (work: () => Promise<unknown>) => {
    setRefused(null);
    setFailure(null);
    setDone(false);
    try {
      await work();
      setDone(true);
    } catch (error) {
      if (error instanceof AppError && (error.status >= 500 || error.code === "NETWORK")) setFailure(error);
      else setRefused(refusalSentence(error));
    }
  };
  return { refused, failure, done, run };
}

export function TalentCommunityTab({ profile, subject }: { profile: CandidateProfileOutput; subject: ProfileSubject }) {
  return profile.community ? <Membership profile={profile} subject={subject} member={profile.community} /> : <NoMembership profile={profile} subject={subject} />;
}

function NoMembership({ profile, subject }: { profile: CandidateProfileOutput; subject: ProfileSubject }) {
  const { can } = useSession();
  const route = useRoute();
  const mayDesignate = can("candidate.community_add") && profile.projection === "full" && profile.status !== "Merged";
  return (
    <EmptyState
      icon="pools"
      sub={PROFILE_RULES.intentionallyAdds}
      action={mayDesignate ? (
        <Button tone="blue" onClick={() => go(withQuery(profileHref(subject, "talent-community"), { action: "designate" }, queryOf(route.raw)))} >
          {ACTION_LABELS.designate}
        </Button>
      ) : undefined}
    >
      {P.designatedMembers}
    </EmptyState>
  );
}

function Membership({ profile, subject, member }: {
  profile: CandidateProfileOutput; subject: ProfileSubject; member: NonNullable<CandidateProfileOutput["community"]>;
}) {
  const { can } = useSession();
  const day = useDay();
  const [lastContact, setLastContact] = useState("");
  const outcome = useOutcome();
  const touch = useWrite(
    (at: string, key) => candidateCommunityTouch(subject.token ? { candidate_id: subject.token, last_contact_at: at } : { ref: subject.ref, last_contact_at: at }, key),
    [["candidate-profile"], ["candidate-search"], ["candidate-timeline"]],
  );
  const prior = member.prior_requisition;
  const items: KeyValueItem[] = [
    [P.reasonDateAdded, `${reasonWord(member.reason_added)} · ${day(member.added_at)}`],
    [P.segment, dash(member.segment)],
    [P.tags, (member.tags ?? []).length > 0 ? (member.tags ?? []).join(", ") : "—"],
    [P.lastContact, day(member.last_contact_at)],
    [P.priorRequisition, prior ? `${prior.requisition_id} · ${prior.title} · ${requisitionWord(prior.state)}` : "—"],
  ];
  if (member.notes !== null && member.notes !== undefined) items.push([P.notes, member.notes, undefined, 3]);
  const mayTouch = can("candidate.community_touch") && profile.status !== "Merged";
  return (
    <Card title={NAV_ITEM_LABELS.talentCommunity} refused={outcome.refused ? { reason: outcome.refused } : undefined}>
      <Stack gap={12}>
        <KeyValue items={items} cols={3} />
        {outcome.failure ? (
          <ErrorStrip message={RULE_LABELS.failedSyncRetryable} problem={{ status: outcome.failure.status, refusal_code: outcome.failure.code, request_id: outcome.failure.requestId }} />
        ) : null}
        {outcome.done ? <Recorded live>{P.lastContact}</Recorded> : null}
        {mayTouch ? (
          <Flex gap={8} align="end" wrap>
            <Field label={P.lastContact}>
              <Input type="date" value={lastContact} onChange={(event) => setLastContact(event.target.value)}  />
            </Field>
            <Button
              tone="primary"
              blocked={lastContact ? undefined : { reason: COMMON_LABELS.required }}
              busy={touch.isPending}
              onClick={() => { void outcome.run(() => touch.mutateAsync(new Date(`${lastContact}T12:00:00Z`).toISOString())); }}
              
            >
              {ACTION_LABELS.save}
            </Button>
          </Flex>
        ) : null}
      </Stack>
    </Card>
  );
}

export function InternalMobilityTab({ profile }: { profile: CandidateProfileOutput }) {
  const blocks = profile.internal ?? [];
  return (
    <Stack gap={12}>
      {blocks.map((block) => {
        const app = (profile.applications ?? []).find((row) => row.application_id === block.application_id);
        return app ? <InternalBlock key={block.application_id} block={block} requisitionId={app.requisition_id} title={`${app.requisition_id} · ${app.title}`} /> : null;
      })}
    </Stack>
  );
}

type MobilityField = "department" | "title" | "leader_name" | "location" | "grade" | "mobility_type";
const FIELDS: readonly { key: MobilityField; label: string; restricted?: boolean }[] = [
  { key: "department", label: P.currentDepartment },
  { key: "title", label: P.title },
  { key: "leader_name", label: P.leader, restricted: true },
  { key: "location", label: L.location },
  { key: "grade", label: P.grade, restricted: true },
  { key: "mobility_type", label: P.mobilityType },
];

function InternalBlock({ block, requisitionId, title }: { block: InternalOut; requisitionId: string; title: string }) {
  const { can } = useSession();
  const day = useDay();
  const mayEdit = can("candidate.set_internal_profile");
  const mobilityTypes = useTaxonomy("mobility_type", mayEdit);
  const [values, setValues] = useState<Record<MobilityField, string>>(() => Object.fromEntries(FIELDS.map(({ key }) => [key, block[key] ?? ""])) as Record<MobilityField, string>);
  const outcome = useOutcome();
  const save = useWrite(
    (input: Parameters<typeof candidateSetInternalProfile>[0], key) => candidateSetInternalProfile(input, key),
    [["candidate-profile"]],
  );
  const attest = useWrite(
    (input: Parameters<typeof candidateRecordAttestation>[0], key) => candidateRecordAttestation(input, key),
    [["candidate-profile"], ["candidate-timeline"]],
  );
  const items: KeyValueItem[] = FIELDS.map(({ key, label }) => [label, dash(block[key])]);
  return (
    <Card title={title} sub={PROFILE_RULES.mobilityCapture} refused={outcome.refused ? { reason: outcome.refused } : undefined}>
      <Stack gap={12}>
        {outcome.failure ? (
          <ErrorStrip message={RULE_LABELS.failedSyncRetryable} problem={{ status: outcome.failure.status, refusal_code: outcome.failure.code, request_id: outcome.failure.requestId }} />
        ) : null}
        {mayEdit ? (
          <>
            <Grid cols={3} gap={12}>
              {FIELDS.map(({ key, label }) => key === "mobility_type" ? (
                // a configured taxonomy, never free text: dashed with his sentence until the Admin publishes its items
                <TaxonomySelect
                  key={key}
                  label={label}
                  taxonomy={mobilityTypes}
                  value={values[key]}
                  onChange={(value) => setValues((prev) => ({ ...prev, [key]: value }))}
                  
                />
              ) : (
                <Field key={key} label={label}>
                  <Input value={values[key]} onChange={(event) => setValues((prev) => ({ ...prev, [key]: event.target.value }))} hs={`cand.mobility.${key.replace(/_/g, "-")}`} />
                </Field>
              ))}
            </Grid>
            <Flex justify="end">
              <Button
                tone="primary"
                busy={save.isPending}
                onClick={() => {
                  void outcome.run(() => save.mutateAsync({
                    requisition_id: requisitionId,
                    application_id: block.application_id,
                    expected_version: block.version ?? null,
                    ...Object.fromEntries(FIELDS.map(({ key }) => [key, values[key].trim() || null])),
                  }));
                }}
                
              >
                {ACTION_LABELS.save}
              </Button>
            </Flex>
          </>
        ) : (
          <KeyValue items={items} cols={3} />
        )}
        <CheckTile
          checked={block.attested ?? false}
          sub={block.attested ? [block.statement, day(block.attested_at)].filter(Boolean).join(" · ") : PROFILE_RULES.attestation}
          can={block.attested || can("candidate.record_attestation")}
          blocked={block.attested ? { reason: PROFILE_RULES.attestation } : undefined}
          onChange={(on) => {
            if (!on || block.attested) return;
            void outcome.run(() => attest.mutateAsync({ requisition_id: requisitionId, application_id: block.application_id, attested: true, source: "app" }));
          }}
          
        >
          {P.selfAttestation}
        </CheckTile>
      </Stack>
    </Card>
  );
}
